import { ChangeEventHandler, Component, MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { TablePagination, TablePaginationProps, Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

// The shop and the customer app render this pager under themes that predate the
// contract, so the values it always had stand in when it is missing.
const LEGACY_FOOTER = { fontSize: 13, controlHeight: 35 };

const styles = (theme: Theme) =>
    createStyles({
        caption: {
            fontSize: `${(theme.tableFooter ?? LEGACY_FOOTER).fontSize}px`,
        },
        select: {
            // The bar is one line of its own; a control that outgrows it makes
            // the foot of every configuration list taller than the foot of every
            // other list.
            minHeight: (theme.tableFooter ?? LEGACY_FOOTER).controlHeight,
            paddingBlock: 0,
            paddingInline: 8,
            textAlign: 'right',
            border: '1px solid',
            borderColor: theme.palette.grey[400],
        },
        input: {
            margin: '0px 10px',
        },
    });

interface PoolTablePaginationProps extends WithStyles<typeof styles> {
    onChangePage: (event: MouseEvent<HTMLButtonElement> | null, page: number) => void;
    onChangeRowsPerPage?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
}

type TablePaginationBaseProps = Omit<TablePaginationProps, 'onPageChange' | 'onRowsPerPageChange'>;

export const PoolTablePagination = withStyles(styles)(
    class PoolTablePaginationBase extends Component<PoolTablePaginationProps & TablePaginationBaseProps> {
        public override render() {
            const { classes, SelectProps, rowsPerPageOptions, page, onChangePage, onChangeRowsPerPage, ...restProps } =
                this.props;

            return (
                <TablePagination
                    // Material-UI page starts at zero, we start with one - adjust input to material-ui system and clip negative values
                    page={Math.max(0, page - 1)}
                    onPageChange={(event, nextPage) => onChangePage(event, nextPage + 1)}
                    onRowsPerPageChange={onChangeRowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions || [5, 10, 30]}
                    classes={{
                        root: classes.caption,
                        input: classes.input,
                    }}
                    SelectProps={{
                        id: 'pagination-page-select',
                        classes: {
                            select: classes.select,
                        },
                        ...SelectProps,
                        variant: 'outlined',
                    }}
                    labelRowsPerPage={<FormattedMessage id="app.common.rowsPerPage" />}
                    {...restProps}
                />
            );
        }
    }
);
